import React, { useId, useState } from 'react';

import '../ActivityRing.css';

import { useResponseWaiting } from './useResponseWaiting';

export type MessageIdentityKind = 'ai' | 'user';

interface MessageIdentityLabelProps {
  className?: string;
  interactive?: boolean;
  isStreaming?: boolean;
  kind?: MessageIdentityKind;
  name: string;
  style?: React.CSSProperties;
  streamContent?: string;
  timestamp?: string;
}

// Kept together so the static and interactive render paths share identical
// icon/name semantics.
// eslint-disable-next-line max-lines-per-function
export const MessageIdentityLabel: React.FC<MessageIdentityLabelProps> = ({
  className = '',
  interactive = true,
  isStreaming = false,
  kind = 'ai',
  name,
  style,
  streamContent = '',
  timestamp,
}) => {
  const [expanded, setExpanded] = useState(false);
  const timestampId = useId();
  const waiting = useResponseWaiting(kind === 'ai' && isStreaming, streamContent);
  const classes = [
    className,
    'message-identity-label',
    `message-identity-label--${kind}`,
    !interactive && 'message-identity-label--static',
    waiting && 'message-identity-label--waiting',
  ].filter(Boolean).join(' ');
  const contents = (
    <>
      <i
        className={`fa-sharp fa-thin ${kind === 'user' ? 'fa-user' : 'fa-robot'} message-identity-label__icon${waiting ? ' activity-ring' : ''}`}
        aria-hidden="true"
      />
      <span className="message-identity-label__name" aria-hidden="true">
        <span>{name}</span>
        {timestamp && <span id={timestampId} className="ai-overview-entry__time">{timestamp}</span>}
      </span>
    </>
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setExpanded(false);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  };

  if (!interactive) {
    return (
      <span
        className={classes}
        style={style}
        role="img"
        aria-label={`Sender: ${name}`}
        aria-busy={waiting || undefined}
        aria-describedby={timestamp ? timestampId : undefined}
      >
        {contents}
      </span>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      style={style}
      aria-label={`Sender: ${name}`}
      aria-busy={waiting || undefined}
      aria-describedby={timestamp ? timestampId : undefined}
      data-expanded={expanded}
      onBlur={() => setExpanded(false)}
      onClick={(event) => {
        event.stopPropagation();
        setExpanded((visible) => !visible);
      }}
      onKeyDown={handleKeyDown}
    >
      {contents}
    </button>
  );
};

export default MessageIdentityLabel;
